import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import qs from 'qs'
import cookies from 'browser-cookies'
import useCartHook from '~/app/components/Cart/CartItemsHooks'
import useRentTimeHook from '~/app/components/Cart/RentTimeHook'
import styled from '~/app/components/styled'
import { Typography } from '@yanolja-rnd/ya-com-core'
import {
  convertCartItemsToOrder,
  filterAvailableChosenOptions,
  updateCartOptions,
} from '~/app/components/Cart/CartOrderHelper'
import conf from '~/app/conf'
import Toolbar from '~/app/components/common/Toolbar/Toolbar'
import CartTabs, { CategoryGroup } from '~/app/components/Cart/CartMisc/CartTabs'
import CartItemSelector from '~/app/components/Cart/CartMisc/CartItemSelector'
import CartGroups from '~/app/components/Cart/CartGroup/CartGroups'
import CartPaymentButton from '~/app/components/Cart/CartMisc/CartPaymentButton'
import YanoljaTerms from '~/app/components/common/Terms/YanoljaTerms'
import { Popup } from '@yanolja-rnd/ya-com-popup'
import Retry from '~/app/components/common/Retry'
import DomesticAccommodationRentTimeLine from '~/app/components/Cart/CartItem/DomesticAccommodationRentTimeLine'
import CartPolicyNotice from '~/app/components/Cart/CartMisc/CartPolicyNotice'
import {
  RentTimeLine,
  ICartItemOption,
  Role,
  CartItemOptionUnavailableReason,
} from '~/__generated__/__types__'
import {
  CartItemsV3Query,
  useCartBenefitBannerQuery,
  useCartItemsV3Query,
  useCartSingleItemsLazyQuery,
  useRemoveFromCartMutation,
  useUpdateCartItemByOptionIdV2Mutation,
} from '~/__generated__/app/query/Cart/Cart.graphql'
import { GoToTop, TextButton, useGoToTop } from '~/app/components'
import { useLoading } from '~/app/components/common/LoadingProvider'
import {
  useCartPageView,
  sendKinesisFilterClick,
  sendKinesisProductClick,
  sendKinesisChangeUseTimeClick,
  sendKinesisOrderClick,
  sendKinesisBannerClick,
  sendKinesisBannerImpr,
  sendKinesisRemoveUnavailableCheckedCartItem,
  sendKinesisLoginClick,
  sendKinesisVipBottomSheetImpr,
  sendKinesisInvalidMemberPopupImpr,
  sendKinesisInvalidMemberPopupClick,
  sendKinesisVipBottomSheetOrderClick,
} from '~/app/kinesis/cart-page'
import { pipe } from 'ramda'
import { cloneDeep } from '@apollo/client/utilities'
import useCartTrainPackageDiscountHooks from '~/app/components/Cart/CartTrainPackageDiscountHooks'
import { getYaNative } from '@yanolja-rnd/ya-native'
import { sendClickCartIntroEvent, sendCriteoCartEvent } from '~/app/analytics-custom-events'
import useVisibilityChange from '~/app/utils/useVisibilityChange'
import usePaymentInfoHooks from '~/app/components/Cart/PaymentInfoHooks'
import CartPaymentInfoSection from '~/app/components/Cart/CartPaymentInfo/CartPaymentInfoSection'
import getMaxQuantityOfLeisureItem from '~/app/utils/getMaxQuantityOfLeisureItem'
import { IsLoginUserDocument } from '~/__generated__/app/query/is-login-user.graphql'
import { ApolloClient, NormalizedCacheObject, useApolloClient } from '@apollo/client'
import { getIsDisabledOrderTrains } from '~/app/utils/TrainHelper'
import { isNative } from '~/app/utils/typeguards'
import * as Sentry from '@sentry/node'
import { sendKinesisClickLogTimeoutAfter } from '~/app/utils/timeout'
import { useDebounceCallback } from '@react-hook/debounce'
import BenefitBanner from '~/app/components/common/BenefitBanner'
import { Toast, ToastHandler } from '@yanolja-rnd/ya-com-toast'
import { VipDiscountBottomSheet } from '~/app/components/common/VipBottomSheet'
import Impression from '~/app/components/common/Impression'
import { Icon_home } from '@yanolja-rnd/ya-com-system-icon'
import useGoHome from '~/app/utils/useGoHome'
import { useDomesticCartCheckValidationMutation } from '~/app/react-query/domestic/cart'

interface CartGroupWrapperProps {
  /** 전체 높이가 채워진 empty state 출력용 */
  fullHeight: boolean
}

/**
 * Legacy Cart Page Component
 * author: jaegeunlee
 * description: 기존 장바구니 페이지 (구 버전)
 */
const Container = styled.div`
  height: 100vh;
`
const CartContainer = styled.div`
  ${Typography('body2').regular};
`
const LoginBanner = styled.div<{ hasItems: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.color.gray50};
  padding: 0 2rem;
  height: 3.8rem;
  ${Typography('body2').regular};
  max-width: ${({ theme }) => theme.unit.PC_WIDTH}px;
  margin: 0 auto ${({ hasItems }) => (hasItems ? '1.2rem' : '0')};
`
const LoginButton = styled(TextButton)`
  &.primary {
    background-color: transparent;
  }
`
const ToolbarItemWrapper = styled.div`
  display: flex;
  min-width: 100%;
`

const CartGroupWrapper = styled.div<CartGroupWrapperProps>`
  width: 100%;
  ${({ fullHeight }) => fullHeight && 'height: 100vh;'}
  background-color: ${({ theme }) => theme.color.white};
  max-width: ${({ theme }) => theme.unit.PC_WIDTH}px;
  margin: 0 auto;
`
const CartPaymentButtonWrapper = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
  box-shadow: 0 0 5px 0 ${({ theme }) => theme.color.black_op16};
  background-color: ${({ theme }) => theme.color.white};
  padding-bottom: env(safe-area-inset-bottom);
`
const CardPaymentContainer = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.unit.PC_WIDTH}px;
  margin: 0 auto;
  padding: 1.6rem 2rem 1.2rem 2rem;
`
const YanoljaTermsWrapper = styled.div`
  max-height: 0;
  margin-top: 12px;
  transition: max-height 300ms ease-in-out;
  overflow: hidden;
  &.visible {
    max-height: 172px;
  }
`
const PaymentEmptySpace = styled.div`
  height: 19rem;
  width: 100%;
`
const HomeIconWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`
const CartPolicyNoticeWrapper = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.unit.PC_WIDTH}px;
  background-color: ${({ theme }) => theme.color.white};
  margin: 0 auto;
  padding: 20px 20px 68px;
  border-bottom: solid 1px ${({ theme }) => theme.color.gray200};
`

interface LegacyCartPageProps {
  viewer: any
}

const LegacyCartPage: React.FC<LegacyCartPageProps> = ({ viewer }) => {
  const toastRef = useRef<ToastHandler>()
  const triggerToast = useCallback(message => {
    toastRef.current?.addMessage({ message, timeout: 3000 })
  }, [])

  // 기존에는 alertMessage.length > 0 조건으로 표기 했으나 타이밍 이슈로 인해 잠깐동안 빈 alert 창이 노출되는 문제가 있어 노출 조건을 별도 변수로 분리
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState<string[]>([]) // 0: title, 1: desc
  const [hasUnavailableItem, setHasUnavailableItem] = useState(false)
  const [hasUnavailableMemberItem, setHasUnavailableMemberItem] = useState(false)
  const [isCheckStockError, setIsCheckStockError] = useState(false)
  const [isVisibleTerms, setIsVisibleTerms] = useState(false)
  const [paymentDisabled, setPaymentDisabled] = useState(false)
  const [currentTab, setTab] = useState<CategoryGroup>(CategoryGroup.ALL)
  const [cartItems, setItems] = useState<CartItemsV3Query['cartItemsV3']>()
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>
  const [removeDialogState, setRemoveDialogState] = useState<{
    removeIds: string[]
    title: string
    description: string
  }>({
    removeIds: [],
    title: '',
    description: '',
  })
  const [isLoginExpireDialogOpen, setIsloginExpireDialogOpen] = useState(false)
  const [redirectQueryString, setRedirectQueryString] = useState('')
  // 회원 등급 상승(일반 -> vip)으로 인한 할인되는 가격
  const [discountPriceByMemberClass, setDiscountPriceByMemberClass] = useState(0)
  const [isOpenVipBottomSheet, setIsOpenVipBottomSheet] = useState(false)

  const { data, loading, error, refetch } = useCartItemsV3Query({
    fetchPolicy: 'no-cache',
    ssr: false,
  })
  const [getCartItemsOption, { loading: isDataForOrderLoading }] = useCartSingleItemsLazyQuery({
    fetchPolicy: 'no-cache',
    ssr: false,
  })

  const { data: bannerData } = useCartBenefitBannerQuery({ ssr: false })

  const { isFabOpen, onClick: onGoToTopClick } = useGoToTop()
  const [isLoading, setLoading] = useState(loading)
  // 페이지 첫접근시 로그인 유무.
  const isFirstAccessLoginUser = useMemo(() => {
    return viewer.role === Role.User
  }, [viewer])

  useCartPageView(isFirstAccessLoginUser, data?.cartItemsV3)
  const [removeFromCart, { loading: removeFromCartLoading }] = useRemoveFromCartMutation()
  const [updateCartItemByOptionIdMutation] = useUpdateCartItemByOptionIdV2Mutation()
  const { mutateAsync: checkCartValidation } = useDomesticCartCheckValidationMutation()

  const router = useRouter()

  const onVisible = useCallback(() => {
    void refetch()
  }, [])

  const isEnabledNative = useMemo(() => {
    if (typeof window !== 'undefined') {
      const yaNative = getYaNative()
      return isNative(yaNative)
    }
    return false
  }, [])

  const { goHome } = useGoHome()
  //const { navigateInternalUrl } = useNavigateInternalUrl()

  useVisibilityChange({
    isActive: isEnabledNative,
    onVisible,
  })

  useEffect(() => {
    if (!data?.cartItemsV3) return
    setCartItems(data.cartItemsV3)
    if (data.cartItemsV3.length === 0) return
    const { cartAmount, cartCategories } = data?.cartItemsV3.reduce(
      (acc, cur: any) => {
        acc.cartCategories[cur.type] = 1
        acc.cartAmount += cur.options?.reduce((acc, cur) => acc + cur.price, 0)
        return acc
      },
      { cartAmount: 0, cartCategories: {} }
    )
    const cartCategoryKeys = Object.keys(cartCategories)

    sendClickCartIntroEvent({
      cart_id: cookies.get('cgntId') || '',
      cart_category: cartCategoryKeys.join(','),
      cart_category_cnt: cartCategoryKeys.length,
      cart_amount: cartAmount,
    })

    sendCriteoCartEvent(data.cartItemsV3)
  }, [data?.cartItemsV3])

  const onChangeTab = useCallback(
    (category: CategoryGroup) => {
      sendKinesisFilterClick(category, isFirstAccessLoginUser)
      setTab(category)
    },
    [currentTab]
  )

  const [isScrolled, setIsScrolled] = React.useState(false)
  const setIsScrolledOnScroll = () => {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
    setIsScrolled(scrollTop > 0)
  }

  /**
   * 스크롤을 하단으로 내렸을때 야놀자 통신 중개업자 텍스트를 노출하도록 함
   */
  const setIsVisibleTermsOnScroll = () => {
    const clientHeight = document.body.clientHeight
    const scrollHeight = document.body.scrollHeight
    const scrollTop =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
    const scrollBottom = scrollHeight - (clientHeight + scrollTop)
    const isUnderThreshold = scrollBottom < 120
    if (isUnderThreshold) {
      setIsVisibleTerms(true)
    } else {
      setIsVisibleTerms(false)
    }
  }

  const onScroll = pipe(setIsVisibleTermsOnScroll, setIsScrolledOnScroll)

  useEffect(() => {
    setIsScrolledOnScroll()
    window.addEventListener('scroll', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  /**
   * 장바구니 아이템에 대한 상태를 제공하는 custom hook
   */
  const {
    currentTabItems,
    currentCount,
    count,
    isVisibleTab,
    totalPrice,
    totalMemberDiscountPriceText,
    isDisabled,
    filterInCurrentTabItems,
  } = useCartHook(cartItems ?? [], currentTab)

  /**
   * 장바구니 묶음할인에 대한 상태를 제공하는 custom hook
   * */
  const { chosenTrainPackageDiscountAmount, isTrainPackageDiscountAvailable } =
    useCartTrainPackageDiscountHooks(cartItems ?? [], count)

  const { maxDiscountTypeName, maxDiscountAmount, isTimeout, isMaxLengthExceeded } =
    usePaymentInfoHooks({
      cartItems: cartItems ?? [],
      maxDiscountLimit: data?.latestAppConfig ?? { maxLength: 4, timeout: 100 },
      isFirstAccessLoginUser,
    })

  const bannerUrl = useMemo(() => {
    return bannerData?.benefitBanner?.image?.url
  }, [bannerData])

  const setCartItems = (items: CartItemsV3Query['cartItemsV3']) => {
    setItems(items.filter(item => item.options.length > 0))
  }

  /**
   * 현재 탭에 나열된 옵션들을 선택/해제 한다
   * 전체 선택되어 있을 경우 전체 해제가 되며 하나라도 선택되지 않은 옵션이 있을 경우 전체 선택이 된다
   * @param checked
   */
  const onCurrentTabCheckAll = (checked: boolean) => {
    const optionIds = filterInCurrentTabItems([...(cartItems ?? [])])
      .map(item => item.options)
      .flat()
      .map(option => option.id)
    const newCartItems = cloneDeep(cartItems ?? [])
    newCartItems.forEach(item => {
      item.options.forEach(option => {
        if (optionIds.includes(option.id)) {
          option.isChosen = checked
        }
      })
    })
    setCartItems(newCartItems)
  }

  /**
   * 단일 옵션에 대해 좌상단의 체크박스를 클릭했을 때 수행되는 콜백
   * @param checked
   * @param id
   */
  const onCheck = (checked: boolean, id: string) => {
    const newCartItems = cloneDeep(cartItems ?? [])
    for (const item of newCartItems) {
      const options: ICartItemOption[] = item.options
      const option = options.find(option => option.id === id)
      if (option) {
        option.isChosen = checked
        break
      }
    }
    setCartItems(newCartItems)
  }

  const onRemoveUnavailableChecked = () => {
    const idList: string[] = []
    currentTabItems.forEach(item => {
      const options = [...item.options] // filter 메서드를 사용하기 위함
      options.filter(o => !o.availability).forEach(o => idList.push(o.id))
    })
    setRemoveDialogState({
      removeIds: idList,
      title: '구매할 수 없는 상품을 모두 삭제할까요?',
      description: '',
    })
    sendKinesisRemoveUnavailableCheckedCartItem()
  }

  /**
   * 현재까지 선택된 옵션을 대상으로 페이지 우상단에 선택 삭제 버튼을 눌렀을 때 수행되는 콜백
   */
  const onRemoveChecked = () => {
    const idList: string[] = []
    currentTabItems.forEach(item => {
      const options = [...item.options]
      options.filter(o => o.isChosen && o.availability).forEach(o => idList.push(o.id))
    })
    setRemoveDialogState({
      removeIds: idList,
      title: `선택한 ${idList.length > 1 ? `${idList.length}개` : ''} 상품을 삭제할까요?`,
      description: '',
    })
  }

  /**
   * 단일 옵션의 우상단 X 버튼을 click 했을 경우 수행하는 콜백
   * @param id
   */
  const onRemove = (id: string) => {
    setRemoveDialogState({
      removeIds: [id],
      title: '삭제하시겠어요?',
      description: `선택하신 상품이 삭제됩니다`,
    })
  }

  /**
   * removeDialogState.removeIds: string[] 배열에 담겨있는 선택된 상품에 대해 API 로 삭제요청을 한다
   * @param buttonId dialog 에 전달하는 버튼의 id
   */
  const onConfirmRemoveClick = useDebounceCallback(
    useCallback(
      (buttonId: string) => {
        if (
          buttonId === 'confirm' &&
          !removeFromCartLoading &&
          // removeDialogState.removeIds.length === 0 이 되고 나서도 클릭이 되는 경우가 있어 추가
          removeDialogState.removeIds.length > 0
        ) {
          if (removeDialogState.removeIds.length === 0) {
            Sentry.captureMessage('장바구니 내 상품 삭제 요청시, 빈 option_ids 가 전달됨')
            return
          }

          removeFromCart({ variables: { input: { option_ids: removeDialogState.removeIds } } })
            .then(res => {
              if (res.data?.removeFromCart === 'EMPTY_OPTION_IDS') {
                Sentry.captureMessage('장바구니 내 상품 삭제 응답 결과, 빈 option_ids 가 전달됨')
                triggerToast('장바구니 항목삭제를 실패 했습니다')
                return
              }
              const newCartItems = cloneDeep(cartItems ?? [])
              for (const item of newCartItems) {
                item.options = (item.options as any).filter(
                  option => !removeDialogState.removeIds.includes(option.id)
                )
              }
              setCartItems(newCartItems)
              if (newCartItems.filter(item => item.options.length > 0).length <= 0) {
                setTab(CategoryGroup.ALL)
              }
              // app 인 경우 상품 삭제가 되었을 때, 장바구니 뱃지의 카운트 노출 sync 요청
              const yaNative = getYaNative()
              if (isNative(yaNative)) {
                yaNative.requestSync({ at: 'cart' })
              }
            })
            .catch(() => {
              triggerToast('장바구니 항목삭제를 실패 했습니다')
            })
            .finally(() => {
              setRemoveDialogState(p => ({
                ...p,
                removeIds: [],
              }))
              return
            })
        } else {
          setRemoveDialogState(p => ({
            ...p,
            removeIds: [],
          }))
        }
      },
      [removeDialogState.removeIds, cartItems, removeFromCartLoading]
    )
  )

  /**
   * 대실시간 변경 BottomSheet 에서 적용하기 버튼을 눌렀을 경우 수행되는 콜백
   * 장바구니 API 를 이용해 이용시간 변경 업데이트를 수행
   * @param rentTime
   */
  const onRentTimeSelect = (rentTime: RentTimeLine) => {
    rent
      .onRentTimeSelect(rentTime)
      ?.then(() => {
        sendKinesisChangeUseTimeClick(rentTime.time)
        triggerToast('이용 시간을 변경했어요')
      })
      ?.catch(() => {
        triggerToast('이용시간을 변경 하지 못했습니다')
      })
      ?.finally(() => {
        rent.setOpenRent(false)
      })
  }

  /**
   * 사용자가 선택 한 수량으로 레저티켓 상품 수량을 업데이트 한다
   * @param quantity 사용자가 선택한 수량
   * @param id 사용자가 수량을 변경한 option id
   */
  const onQuantityChange = useCallback(
    (quantity: number, id: string) => {
      const newCartItems = cloneDeep(cartItems ?? [])
      for (const item of newCartItems) {
        const option = item.options.flat().find(option => option.id === id)
        if (option && option.__typename === 'DomesticLeisureCartItemOption') {
          const maxQuantity = getMaxQuantityOfLeisureItem(option)
          option.quantity = quantity
          setCartItems(newCartItems)
          updateQuantity({ quantity, id, maxQuantity })
          break
        }
      }
    },
    [cartItems]
  )

  const updateQuantity = useDebounceCallback(
    ({ quantity, id, maxQuantity }: { quantity: number; id: string; maxQuantity: number }) => {
      const input = {
        optionId: id,
        domesticLeisure: {
          quantity,
        },
      }

      updateCartItemByOptionIdMutation({
        variables: { input },
      })
        .then(async ({ data }) => {
          const { cartSingleItems, errorCode } = data?.updateCartItemByOptionIdV2 ?? {}
          if (errorCode) {
            // 업데이트에 오류가 있다면 원복이 필요하다
            const updatedCartItems = updateCartOptions(cartItems ?? [], cartSingleItems)
            setCartItems(updatedCartItems)

            if (errorCode === 'CART_MODIFY_REQUEST_001') {
              triggerToast(`이 상품은 최대 ${maxQuantity}개까지 구매할 수 있어요`)
              return
            }
            if (errorCode === 'CART_MODIFY_REQUEST_003') {
              // 동일한 수량으로 변경 요청시 응답받는 코드
              // 해당 case 에서 toast 메시지를 노출할 필요는 없다
              return
            }
            triggerToast('수량 업데이트 중 오류가 발생했습니다')
          }
        })
        .catch(() => {
          // mutation 오류
          triggerToast('수량 업데이트 중 오류가 발생했습니다')
        })
    },
    500
  )

  /**
   * 장바구니 목록을 얻어오지 못한경우 재시도 할 수 있는 버튼이 노출되며 이 버튼을 누를 경오 호출되는 콜백
   */
  const onRetryClick = () => {
    setHasUnavailableItem(false)
    setIsCheckStockError(false)
    setLoading(true)
    refetch()
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  const onClickInvalidMemberPopup = useCallback(() => {
    sendKinesisInvalidMemberPopupClick()
    setHasUnavailableMemberItem(false)
    setLoading(true)
    refetch()
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [cartItems])

  const onOrderClick = () => {
    const cgntId = cookies.get('cgntId') || ''
    if (!cgntId) {
      triggerToast('장바구니 정보를 확인할 수 없습니다. 다시 시도해 주세요')
      return
    }

    /**
     * YAWEB-520
     * 묶음할인 어뷰징 대응: 기차 + 레저 주문 불가
     * 기차 + 레저 + (숙박|버스) 주문 가능
     */
    const { isDisabledByOnlyTrains, isDisabledByItems, disabledOrderTrainMessage } =
      getIsDisabledOrderTrains({
        propertyCount: count.availableCheckedAccommodation,
        leisureCount: count.availableCheckedTicket + count.availableCheckedRentACar,
        trainCount: count.availableCheckedTrain,
        busCount: count.availableCheckedBus,
      })

    if (isDisabledByItems || isDisabledByOnlyTrains) {
      setAlertMessage(disabledOrderTrainMessage)
      setAlertOpen(true)
      return
    }

    onOrder()
  }

  const onLoginExpireDialogButtonClick = useCallback(() => {
    setIsloginExpireDialogOpen(false)
    const redirect = `${window.location.origin}/orders/new?${redirectQueryString}`
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      // yaNative.login 의 url 옵션은 해당 메서드가 호출된 기존 웹뷰를 해당 url 로 갱신한다.
      yaNative.wv({
        method: 'get',
        url: `${window.location.origin}/hooks/login`,
        params: {
          redirect,
        } as any,
      })
    } else {
      const encodedRedirect = encodeURIComponent(redirect)
      router.push(`/hooks/login?redirect=${encodedRedirect}`)
    }
  }, [redirectQueryString])

  const onOrder = async () => {
    setPaymentDisabled(true)
    const { lng = '', lat = '' } = router.query as NodeJS.Dict<string>
    const trackCode =
      (router.query.trackCode as string) ||
      cookies.get('trackCode') ||
      cookies.get('trackcode') ||
      ''
    const cgntId = cookies.get('cgntId') || ''

    // 장바구니에서 선택한 옵션들
    const chosenOptions = (cartItems ?? [])
      .map(item => item.options)
      .flat()
      .filter(filterAvailableChosenOptions)

    // 개별 옵션 재조회
    const updatedOptions = await getCartItemsOption({
      variables: {
        input: chosenOptions.map(option => ({ optionId: option.id })),
      },
    }).then(res => res.data?.cartSingleItems || [])

    // 업데이트된 장바구니 아이템들
    const updatedCartItems = updateCartOptions(cartItems ?? [], updatedOptions)
    const updatedChosenCartItems = updatedCartItems
      .map(item => item.options)
      .flat()
      .filter(option => chosenOptions.find(chosenOption => chosenOption.id === option.id))
    setCartItems(updatedCartItems)

    const orderItems = convertCartItemsToOrder({
      cgntId,
      items: updatedCartItems,
      trackCode,
      lng,
      lat,
    })
    const meta = orderItems.meta

    /** 선택한 장바구니 상품으로 마스터 재고 체크 */
    try {
      await checkCartValidation({
        optionIds: updatedChosenCartItems.map(option => option.id),
      })
    } catch (error) {
      setIsCheckStockError(true)
      setPaymentDisabled(false)
      return
    }

    /**
     * 주문을 원하는 장바구니 항목 중 더이상 주문을 할 수 없는 항목이 있을 경우
     * 주문 불가 메세지를 노출 하고 다시 선택할 수 있도록 한다
     */
    // 업데이트된 선택한 장바구니 options
    for (const option of updatedChosenCartItems) {
      const isInvalidMember =
        option.unavailableReason === CartItemOptionUnavailableReason.InvalidMemberClass

      if (!option.availability || option.outdated) {
        setIsOpenVipBottomSheet(false)
        isInvalidMember ? setHasUnavailableMemberItem(true) : setHasUnavailableItem(true)
        setPaymentDisabled(false)
        return
      }
    }

    if (!isOpenVipBottomSheet) {
      // 회원 등급 상승으로 인해 할인되는 가격 계산
      const discountPrice = updatedChosenCartItems.reduce((acc, option) => {
        if (
          option.__typename === 'DomesticAccommodationCartItemOption' &&
          option.itemUpdatedByMemberClass &&
          option.originalPrice
        ) {
          return acc + (option.originalPrice - option.price)
        }
        return acc
      }, 0)
      setDiscountPriceByMemberClass(discountPrice)

      if (!!discountPrice) {
        setIsOpenVipBottomSheet(true)
        setPaymentDisabled(false)
        return
      }
    }

    isOpenVipBottomSheet
      ? sendKinesisVipBottomSheetOrderClick(updatedCartItems)
      : sendKinesisOrderClick(
          isFirstAccessLoginUser,
          updatedCartItems,
          chosenTrainPackageDiscountAmount,
          trackCode
        )

    setTimeout(() => {
      setPaymentDisabled(false)
    }, 3000)

    /**
     * 주문할 상품이 없는 경우
     */
    if (!orderItems.items.length) {
      return
    }

    const querystring = qs.stringify([meta, ...orderItems.items], { encodeValuesOnly: true })

    if (isFirstAccessLoginUser) {
      const {
        data: { isLoginUser: isCurrentLoginUser },
      } = await apolloClient.query({ query: IsLoginUserDocument })
      // 로그인 세션 만료
      if (!isCurrentLoginUser) {
        setIsloginExpireDialogOpen(true)
        setRedirectQueryString(querystring)
        return
      }
    }

    //nextjs chunk cache 오류
    //navigateInternalUrl(`/orders/new?${querystring}`)
    const yaNative = getYaNative()
    const path = `/orders/new?${querystring}`

    if (isNative(yaNative)) {
      const [urlPath, params] = path.split('?')
      yaNative.wv({
        method: 'get',
        url: `${window.location.origin}${urlPath}`,
        ...(params && { params: qs.parse(params, { depth: 0 }) as any }),
      })
    } else {
      location.href = path
    }
  }

  const onBackClick = () => {
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.dismiss()
    } else {
      router.back()
    }
  }

  const onBannerClick = useCallback(async () => {
    const action = bannerData?.benefitBanner?.action
    const logMeta = bannerData?.benefitBanner?.logMeta
    const yaNative = getYaNative()
    if (isNative(yaNative) && action?.app) {
      await sendKinesisClickLogTimeoutAfter(() =>
        sendKinesisBannerClick(
          logMeta?.id || '',
          logMeta?.objectType || '',
          logMeta?.title || '',
          action.app || ''
        )
      )
      yaNative.deepLink({ link: action.app })
    } else if (action?.web) {
      await sendKinesisClickLogTimeoutAfter(() =>
        sendKinesisBannerClick(
          logMeta?.id || '',
          logMeta?.objectType || '',
          logMeta?.title || '',
          action.web || ''
        )
      )
      location.href = action.web
    }
  }, [bannerData])

  const onLoginClick = useCallback(async () => {
    await sendKinesisClickLogTimeoutAfter(sendKinesisLoginClick)
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.login()
    } else {
      location.href = `${conf.web.url}/login?redirect=${window.location.href}`
    }
  }, [])

  const onAccommodationSearchClick = () => {
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.deepLink({ link: `${conf.app.search.url}?tab=place` })
    } else {
      location.href = `${conf.web.url}/search?tab=place`
    }
  }

  const onTicketSearchClick = () => {
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.deepLink({ link: `${conf.app.search.url}?tab=leisure` })
    } else {
      location.href = `${conf.web.url}/search?tab=leisure`
    }
  }

  const onTransportationSearchClick = () => {
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.deepLink({ link: conf.app.transportation.url })
    } else {
      location.href = `${conf.web.url}/sub-home/transportation`
    }
  }

  const onBannerImpressed = useCallback(() => {
    const logMeta = bannerData?.benefitBanner?.logMeta
    sendKinesisBannerImpr(logMeta?.id || '', logMeta?.objectType || '', logMeta?.title || '')
  }, [bannerData])

  const onTitleClick = async (id: string) => {
    await sendKinesisClickLogTimeoutAfter(() =>
      sendKinesisProductClick(isFirstAccessLoginUser, id, cartItems ?? [])
    )
    for (const item of cartItems ?? []) {
      if (item.__typename === 'DomesticAccommodationCartItem') {
        if (!item.property.data.published) {
          /**
           * 노출안함으로 되어 있는 상품 PDP 접근 시 인터넷 연결 불가로 표현하기 때문에 이동하지 않도록 예외처리 필요
           * https://yanolja.atlassian.net/browse/DEFECT-8063
           */
          return
        }
        const option = item.options.find(option => option.id === id)
        if (option) {
          const url = `${conf.place.url}/places/${item.propertyId}`
          const yaNative = getYaNative()
          if (isNative(yaNative)) {
            yaNative.wv({
              method: 'get',
              url,
            })
          } else {
            location.href = url
          }
        }
      } else if (item.__typename === 'DomesticLeisureCartItem') {
        const option = item.options.find(option => option.id === id)
        if (option) {
          const yaNative = getYaNative()
          if (isNative(yaNative)) {
            yaNative.deepLink({ link: `yanoljamotel://leisure/detail?idx=${item.dealId}` })
          } else {
            location.href = `${conf.leisure.url}/leisure/${item.dealId}`
          }
          break
        }
      } else if (item.__typename === 'DomesticTrainCartItem') {
        const option = item.options.find(option => option.id === id)
        if (option) {
          const train = option.trains[0]
          if (!train) {
            break
          }
          const params = {
            departureStationCode: train.departureStationCode,
            arrivalStationCode: train.arrivalStationCode,
            departureDate: DateTime.fromISO(train.departureAt).toFormat('yyyy-LL-dd'),
            ...(option.trains.length > 1
              ? {
                  returnDate: DateTime.fromISO(option.trains[1].departureAt).toFormat('yyyy-LL-dd'),
                }
              : {}),
            tripType: option.trains.length > 1 ? 'round' : 'oneway',
          }

          const sHomeQuery = qs.stringify(params, { encodeValuesOnly: true })
          const sHomeUrl = `${conf.web.url}/train`
          const yaNative = getYaNative()
          if (isNative(yaNative)) {
            yaNative.wv({
              method: 'get',
              url: `${sHomeUrl}`,
              params,
            })
          } else {
            location.href = `${sHomeUrl}?${sHomeQuery}`
          }
          break
        }
      } else if (item.__typename === 'DomesticBusCartItem') {
        const option = item.options.find(option => option.id === id)
        if (option) {
          const reservation = option.reservations[0]
          if (!reservation) {
            break
          }
          const params = {
            departureCode: reservation.departureTerminalNo,
            departureName: reservation.departureTerminalName,
            arrivalCode: reservation.arrivalTerminalNo,
            arrivalName: reservation.arrivalTerminalName,
            departureDate: reservation.departureDate,
            arrivalDate: reservation.expectedArriveAt,
            tripType: option.reservations.length > 1 ? 'round' : 'oneway',
          }
          const yaNative = getYaNative()
          if (isNative(yaNative)) {
            yaNative.deepLink({
              link: `yanoljamotel://exbus?${qs.stringify(params, { encodeValuesOnly: true })}`,
            })
          } else {
            setAlertMessage(['고속버스 상품은 앱에서만 확인할 수 있어요'])
            setAlertOpen(true)
          }
          break
        }
      }
    }
  }

  // 묶음할인 상태, isVisibleTerms, 화면 너비에 따른 CTA 영역 높이 offset 계산 memo
  // position: sticky 를 사용하면 아래와 같은 로직이 필요없지만 IE는 sticky 를 지원하지 않는다.
  const ctaHeightOffset = useMemo(() => {
    let isTermsMoreThan3Lines = false
    if (typeof window !== 'undefined') {
      isTermsMoreThan3Lines = window.innerWidth <= 432
    }
    const result = isVisibleTerms ? (isTermsMoreThan3Lines ? 18.2 : 16.4) : 12.8
    return count.availableCheckedTrain ? result + 2.8 : result
  }, [isVisibleTerms, typeof window !== 'undefined', count])

  /**
   * 대실시간 변경을 위한 상태와 함수를 제공하는 custom hook
   */
  const rent = useRentTimeHook(cartItems ?? [], setCartItems)

  useEffect(
    () => setLoading(loading || isDataForOrderLoading || rent.isLoading),
    [loading, isDataForOrderLoading, rent.isLoading]
  )
  useLoading(isLoading)

  return (
    <Container>
      <Toolbar
        title={'장바구니'}
        onClickBackButton={onBackClick}
        hasShadow={isScrolled}
        rightAppended={
          <HomeIconWrapper onClick={goHome}>
            <Icon_home size={2.4} unit="rem" />
          </HomeIconWrapper>
        }
      >
        {!isFirstAccessLoginUser && (
          <LoginBanner hasItems={count.total > 0}>
            <span>로그인하고 NOL 회원 전용 특가를 확인해 보세요</span>
            <LoginButton title="로그인" type="primary" typography="body2" onClick={onLoginClick} />
          </LoginBanner>
        )}
        {count.total > 0 && (
          <>
            {!isScrolled && (
              <ToolbarItemWrapper>
                <CartTabs
                  currentTab={currentTab}
                  totalCount={count.total}
                  accommodationCount={count.accommodation}
                  ticketCount={count.ticket}
                  trainCount={count.train}
                  busCount={count.bus}
                  rentACarCount={count.rentACar}
                  onChangeTab={onChangeTab}
                />
              </ToolbarItemWrapper>
            )}
            {isVisibleTab && (
              <ToolbarItemWrapper>
                <CartItemSelector
                  onCheck={onCurrentTabCheckAll}
                  onRemove={onRemoveChecked}
                  onRemoveUnavailable={onRemoveUnavailableChecked}
                  count={count}
                  currentCount={currentCount}
                />
              </ToolbarItemWrapper>
            )}
          </>
        )}
      </Toolbar>
      <Retry onRetryClick={onRetryClick} isDisabled={isLoading} hasError={!!error}>
        {!!cartItems && (
          <CartContainer>
            <CartGroupWrapper fullHeight={currentTabItems.length <= 0}>
              <CartGroups
                isTrainPackageDiscountAvailable={isTrainPackageDiscountAvailable}
                items={currentTabItems}
                onCheck={onCheck}
                onRentTimeClick={rent.onRentTimeChangeClick}
                onQuantityChange={onQuantityChange}
                onClose={onRemove}
                onHomeClick={goHome}
                onAccommodationSearchClick={onAccommodationSearchClick}
                onTicketSearchClick={onTicketSearchClick}
                onTransportationSearchClick={onTransportationSearchClick}
                onTitleClick={onTitleClick}
                onCancelRefreshButtonClick={onRetryClick}
                tab={currentTab}
              />
            </CartGroupWrapper>
            {currentTabItems.length > 0 && (
              <>
                <CartPaymentInfoSection
                  maxDiscountAmount={maxDiscountAmount}
                  maxDiscountTypeName={maxDiscountTypeName}
                  isTimeout={isTimeout}
                  isMaxLengthExceeded={isMaxLengthExceeded}
                  totalPrice={totalPrice}
                />
                {bannerUrl && (
                  <BenefitBanner
                    url={bannerUrl}
                    onImpressed={onBannerImpressed}
                    onClick={onBannerClick}
                  />
                )}
                <CartPolicyNoticeWrapper>
                  <CartPolicyNotice />
                </CartPolicyNoticeWrapper>
                <PaymentEmptySpace />
              </>
            )}
            <GoToTop
              isOpened={isFabOpen}
              onClick={onGoToTopClick}
              bottom={ctaHeightOffset + 2}
              zIndex={'unset'}
            />
            {!!cartItems && currentTabItems.length > 0 && (
              <CartPaymentButtonWrapper>
                <CardPaymentContainer>
                  <CartPaymentButton
                    count={count.availableChecked}
                    trainCheckedCount={count.availableCheckedTrain}
                    price={totalPrice - maxDiscountAmount}
                    totalMemberDiscountPriceText={totalMemberDiscountPriceText}
                    disabled={isLoading || paymentDisabled || isDisabled}
                    onOrderClick={onOrderClick}
                  />
                  <YanoljaTermsWrapper className={isVisibleTerms ? 'visible' : ''}>
                    <YanoljaTerms />
                  </YanoljaTermsWrapper>
                </CardPaymentContainer>
              </CartPaymentButtonWrapper>
            )}
          </CartContainer>
        )}
      </Retry>
      <DomesticAccommodationRentTimeLine
        isOpened={rent.isOpenRent}
        setOpened={rent.setOpenRent}
        noticeText={rent.noticeText}
        rentTime={rent.rentTime}
        rentTimeLine={rent.timeLines}
        onTimeSelect={onRentTimeSelect}
      />
      <Popup
        popupId={'CART_REMOVE_POPUP'}
        buttons={[
          { id: 'dismiss', title: '아니요' },
          { id: 'confirm', title: '삭제하기', isPrimary: true, testId: 'cart-remove-confirm' },
        ]}
        isOpened={!!removeDialogState.removeIds.length}
        title={removeDialogState.title}
        description={removeDialogState.description}
        onButtonClick={onConfirmRemoveClick}
      />
      <Popup
        popupId={'CART_UNAVAILABLE_POPUP'}
        buttons={[{ id: 'confirm', title: '확인', isPrimary: true }]}
        isOpened={hasUnavailableItem}
        title={'구매할 수 없는 상품이 있어요'}
        description={'장바구니에 담긴 상품을 다시 확인해주세요.'}
        onButtonClick={onRetryClick}
      />
      <Popup
        popupId={'CART_UNAVAILABLE_CHECK_STOCK_POPUP'}
        buttons={[{ id: 'confirm', title: '확인', isPrimary: true }]}
        isOpened={isCheckStockError}
        title={'품절로 예약이 불가한 숙소 상품이\n포함되어 있습니다.'}
        description={'예약 상품을 다시 확인해주세요.'}
        onButtonClick={onRetryClick}
      />
      {hasUnavailableMemberItem && (
        <Impression onImpressed={sendKinesisInvalidMemberPopupImpr}>
          <Popup
            popupId={'CART_UNAVAILABLE_MEMBER_CLASS_POPUP'}
            buttons={[{ id: 'confirm', title: '확인', isPrimary: true }]}
            isOpened={hasUnavailableMemberItem}
            title={'예약이 불가한 상품이 포함되어 있습니다'}
            description={'예약 상품을 다시 확인해주세요'}
            onButtonClick={onClickInvalidMemberPopup}
          />
        </Impression>
      )}
      <Popup
        popupId={'CART_POPUP'}
        buttons={[{ id: 'confirm', title: '확인', isPrimary: true }]}
        isOpened={alertOpen}
        title={alertMessage[0] || ''}
        description={alertMessage[1] || ''}
        onButtonClick={() => setAlertOpen(false)}
      />
      <Popup
        popupId={'CART_LOGIN_EXPIRE_POPUP'}
        buttons={[{ id: 'confirm', title: '확인', isPrimary: true }]}
        isOpened={isLoginExpireDialogOpen}
        title={'로그인 정보가 만료되었습니다. 로그인 화면으로 이동합니다.'}
        onButtonClick={onLoginExpireDialogButtonClick}
      />
      <Toast toastRef={toastRef} />
      <VipDiscountBottomSheet
        isOpen={isOpenVipBottomSheet}
        discountPrice={discountPriceByMemberClass}
        onOrder={onOrder}
        onClose={() => setIsOpenVipBottomSheet(false)}
        onImpressed={() => sendKinesisVipBottomSheetImpr(cartItems ?? [])}
      />
    </Container>
  )
}

export default LegacyCartPage
